import clsx from 'clsx'

export interface BadgeProps {
  label: string
  color?: 'sky' | 'mint' | 'gold' | 'orange' | 'indigo' | 'lavender' | 'white' | 'brick' | 'sand'
  leftIcon?: string
  rightIcon?: string
  wrapLabel?: boolean
  /** Corner style — 'default' matches the standard badge, 'pill' fully rounds the corners. */
  shape?: 'default' | 'pill'
  className?: string
  /** When provided, the badge renders as a button (e.g. a "+N more" / "Show less" toggle). */
  onClick?: () => void
}

export function Badge({
  label,
  color,
  leftIcon,
  rightIcon,
  wrapLabel = true,
  shape = 'default',
  className,
  onClick,
}: BadgeProps) {
  const getBackgroundColor = (color?: string) => {
    const colorMap = {
      brick: 'bg-brick',
      sky: 'bg-sky-50',
      mint: 'bg-mint-50',
      gold: 'bg-gold-50',
      orange: 'bg-orange-25',
      indigo: 'bg-indigo',
      lavender: 'bg-lavender-50',
      white: 'bg-white',
      sand: 'bg-sand',
    }
    return color ? colorMap[color as keyof typeof colorMap] || 'bg-white-dark' : 'bg-white-dark'
  }

  const getLabelColor = (bgColor: string) => {
    const colorMap = {
      'bg-sky-50': 'text-indigo',
      'bg-mint-50': 'text-forest',
      'bg-gold-50': 'text-brick',
      'bg-orange-25': 'text-brick',
      'bg-indigo': 'text-white',
      'bg-brick': 'text-white',
      'bg-lavender-50': 'text-indigo',
    }
    return colorMap[bgColor as keyof typeof colorMap] || 'text-charcoal'
  }

  const bgColor = getBackgroundColor(color)
  const textColor = getLabelColor(bgColor)

  const classes = clsx(
    // Base styles
    'inline-flex items-center gap-2 py-1 px-2 border text-sm font-sans font-normal leading-tight border-edge-stroke',
    shape === 'pill' ? 'rounded-full' : 'rounded',
    // Background and text colors
    bgColor,
    textColor,
    // Text wrapping
    wrapLabel ? 'whitespace-normal max-w-[200px]' : 'whitespace-nowrap',
    // Interactive state
    onClick &&
      'cursor-pointer hover:bg-sand-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest',
    // Additional classes
    className
  )

  const content = (
    <>
      {leftIcon && <i className={`fa-solid fa-${leftIcon}`} />}
      {label}
      {rightIcon && <i className={`fa-solid fa-${rightIcon}`} />}
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    )
  }

  return <div className={classes}>{content}</div>
}
